import { ButtonHTMLAttributes, forwardRef, JSX } from 'react'
import { COLORS } from '~theme/colors'
import { Fonts } from '~theme/deprecated-vt/deprecated-vt-theme'

const SPACINGS = [
  '4px',
  '8px',
  '16px',
  '24px',
  '32px',
  '40px',
  '48px',
  '56px',
  '64px',
  '72px',
  '80px',
  '88px',
  '96px',
  '104px',
  '112px',
  '120px',
  '128px',
  '136px',
  '144px',
  '152px',
  '160px',
  '168px',
  '176px',
  '184px',
  '192px',
  '200px',
]

// eslint-disable-next-line
import styled from 'styled-components'

export interface ClickPointProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  ariaLabel: string
  children: JSX.Element
  className?: string
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void
  variant?: 'light' | 'dark'
}

const StyledClickPoint = styled.button`
  align-items: center;
  appearance: none;
  background-color: transparent;
  border-radius: 50%;
  border: 0px;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: ${Fonts.sizes.xLarge};
  justify-content: center;
  margin: 0px;
  outline: 0px;
  overflow: visible;
  padding: ${SPACINGS[1]};
  position: relative;
  text-align: center;
  text-decoration: none;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  user-select: none;
  vertical-align: middle;

  &:hover {
    background-color: ${COLORS.greys.ultraLight};
  }

  &:active {
    background-color: ${COLORS.greys.light};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.secondary.lightBlue};
  }
`

const StyledClickPointDark = styled(StyledClickPoint)`
  & svg path {
    fill: ${COLORS.primary.white};
  }
  &:hover {
    background-color: ${COLORS.secondary.darkBlue};
  }

  &:active {
    background-color: ${COLORS.secondary.darkBlue};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.secondary.lightBlue};
  }
`

const clickPointComponentVariants = {
  light: StyledClickPoint,
  dark: StyledClickPointDark,
}

export const ClickPoint = forwardRef<HTMLButtonElement, ClickPointProps>(
  function ClickPoint(
    { ariaLabel, className, children, onClick, variant = 'light' },
    ref,
  ) {
    const StyledVariantClickPoint = clickPointComponentVariants[variant]
    return (
      <StyledVariantClickPoint
        onClick={onClick}
        aria-label={ariaLabel}
        className={className}
        ref={ref}
        type="button"
      >
        {children}
      </StyledVariantClickPoint>
    )
  },
)

ClickPoint.displayName = 'ClickPoint'
